import { GlassCard } from "@/components/GlassCard";
import { fmtUSDcents } from "@/lib/format";
import { ExternalLink, Phone, Pencil, Trash2, Search, Plus } from "lucide-react";
import { useMemo, useState } from "react";

export function SalesTable({ sales, rate, onEdit, onDelete, onAdd }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q) return sales;
    const t = q.toLowerCase();
    return sales.filter(
      (s) =>
        s.customer_name.toLowerCase().includes(t) ||
        (s.phone || "").toLowerCase().includes(t) ||
        (s.notes || "").toLowerCase().includes(t)
    );
  }, [sales, q]);

  return (
    <GlassCard data-testid="sales-table">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <p className="font-heading text-base font-bold text-white">Sales Log</p>
          <p className="text-xs text-white/55">
            {sales.length} entries · commission @ {Math.round(rate * 100)}%
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="glass-input pl-9 pr-3 py-2 rounded-full text-sm w-40 sm:w-56"
              data-testid="sales-search"
            />
          </div>
          <button
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 px-4 py-2 text-sm font-semibold active:opacity-80"
            data-testid="add-sale-btn"
          >
            <Plus className="w-4 h-4" /> Add Sale
          </button>
        </div>
      </div>

      <div className="overflow-x-auto -mx-2 px-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-white/50 border-b border-white/15">
              <th className="py-2.5 pr-2 font-semibold">Date</th>
              <th className="py-2.5 pr-2 font-semibold">Customer</th>
              <th className="py-2.5 pr-2 font-semibold">Phone</th>
              <th className="py-2.5 pr-2 text-right font-semibold">Sale</th>
              <th className="py-2.5 pr-2 text-right font-semibold">Comm.</th>
              <th className="py-2.5 pr-2 text-right font-semibold">Bonus</th>
              <th className="py-2.5 pr-2 text-right font-semibold">Total</th>
              <th className="py-2.5 pr-2 text-right font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-white/40">
                  No sales yet. Click <span className="text-white/80 font-medium">&ldquo;Add Sale&rdquo;</span> to log your first.
                </td>
              </tr>
            )}
            {filtered.map((s) => {
              const comm = s.amount * rate;
              const total = comm + (s.bonus || 0);
              return (
                <tr
                  key={s.id}
                  className="border-b border-white/8 hover:bg-white/5 group"
                  data-testid={`sale-row-${s.id}`}
                >
                  <td className="py-3 pr-2 text-white/60 font-mono text-xs">{s.sale_date}</td>
                  <td className="py-3 pr-2">
                    {s.salesforce_url ? (
                      <a
                        href={s.salesforce_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white font-medium hover:text-sky-300 inline-flex items-center gap-1"
                        data-testid={`salesforce-link-${s.id}`}
                      >
                        {s.customer_name}
                        <ExternalLink className="w-3 h-3 opacity-60" />
                      </a>
                    ) : (
                      <span className="text-white font-medium">{s.customer_name}</span>
                    )}
                    {s.notes && <p className="text-xs text-white/40 mt-0.5">{s.notes}</p>}
                  </td>
                  <td className="py-3 pr-2 text-white/70">
                    {s.phone ? (
                      <a href={`tel:${s.phone}`} className="hover:text-sky-300 inline-flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {s.phone}
                      </a>
                    ) : (
                      <span className="text-white/25">—</span>
                    )}
                  </td>
                  <td className="py-3 pr-2 text-right font-mono tabular-nums text-white">{fmtUSDcents(s.amount)}</td>
                  <td className="py-3 pr-2 text-right font-mono tabular-nums text-sky-300">{fmtUSDcents(comm)}</td>
                  <td className="py-3 pr-2 text-right font-mono tabular-nums text-rose-300">
                    {s.bonus ? fmtUSDcents(s.bonus) : <span className="text-white/25">—</span>}
                  </td>
                  <td className="py-3 pr-2 text-right font-mono tabular-nums font-semibold text-emerald-300">
                    {fmtUSDcents(total)}
                  </td>
                  <td className="py-3 pr-2 text-right">
                    <div className="flex justify-end gap-1 opacity-50 group-hover:opacity-100">
                      <button
                        onClick={() => onEdit(s)}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-white/70"
                        data-testid={`edit-sale-${s.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => onDelete(s)}
                        className="p-1.5 rounded-lg hover:bg-rose-500/20 text-rose-300"
                        data-testid={`delete-sale-${s.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </GlassCard>
  );
}
